import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { getCourseDetail, getProgress } from "@/services/api";
import Layout from "@/components/Layout";
import {
  ChevronLeft, CheckCircle2, Lock, PlayCircle, FileText, Award, ChevronRight
} from "lucide-react";

export default function CourseDetailPage() {
  const { courseId } = useParams();
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [cRes, pRes] = await Promise.all([
          getCourseDetail(token, decodeURIComponent(courseId)),
          getProgress(token, user.id),
        ]);
        setCourse(cRes.data);
        const prog = (pRes.data || []).find(
          (p) => p.course_identifier === decodeURIComponent(courseId)
        );
        setProgress(prog || null);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [courseId, token, user]);

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-64" style={{ color: "#242220" }}>
          <p className="font-bold text-lg opacity-40">Betöltés...</p>
        </div>
      </Layout>
    );
  }

  if (!course) {
    return (
      <Layout>
        <div className="text-center py-16">
          <p className="font-bold" style={{ color: "#242220" }}>Kurzus nem található.</p>
          <button onClick={() => navigate("/dashboard")} className="mt-4 text-sm underline" style={{ color: "#242220" }}>
            Vissza a vezérlőpultra
          </button>
        </div>
      </Layout>
    );
  }

  const lessons = course.lessons || [];
  const completedIds = progress?.lessons_completed || [];
  const examPassed = progress?.exam_passed || false;
  const allLessonsDone = lessons.length > 0 && lessons.every((l) => completedIds.includes(l.id));

  const pct = lessons.length > 0
    ? Math.round((completedIds.length / lessons.length) * 100)
    : 0;

  return (
    <Layout>
      <div data-testid="course-detail-page">
        {/* Back button */}
        <button
          onClick={() => navigate("/dashboard")}
          className="flex items-center gap-2 mb-6 font-bold text-sm transition-all hover:gap-3"
          style={{ color: "#242220", background: "none", border: "none", cursor: "pointer" }}
          data-testid="back-to-dashboard-btn"
        >
          <ChevronLeft size={18} strokeWidth={2.5} />
          Vissza a vezérlőpultra
        </button>

        {/* Hero */}
        <div
          className="relative mb-8 p-6 lg:p-8 overflow-hidden"
          style={{
            background: "#FBD556",
            borderRadius: "24px",
            border: "1.5px solid #242220",
            boxShadow: "6px 6px 0 #242220",
          }}
        >
          <div className="flex flex-col lg:flex-row gap-6 items-start lg:items-center">
            {course.cover_image && (
              <img
                src={course.cover_image}
                alt={course.name}
                className="w-full lg:w-48 object-cover flex-shrink-0"
                style={{ borderRadius: "16px", border: "1.5px solid #242220", height: 140 }}
              />
            )}
            <div className="flex-1">
              <h1
                className="text-2xl lg:text-3xl font-bold mb-2"
                style={{ color: "#242220" }}
              >
                {course.name}
              </h1>
              <p className="text-sm mb-4" style={{ color: "#242220", opacity: 0.7 }}>
                {course.description}
              </p>

              <div className="flex items-center gap-4 flex-wrap">
                <span className="flex items-center gap-1 text-sm font-bold" style={{ color: "#242220" }}>
                  <FileText size={15} strokeWidth={2.5} />
                  {lessons.length} lecke
                </span>
                <span className="flex items-center gap-1 text-sm font-bold" style={{ color: "#242220" }}>
                  <PlayCircle size={15} strokeWidth={2.5} />
                  {course.final_exam?.time_limit_minutes || 30} perces záróvizsga
                </span>
                {examPassed && (
                  <span
                    className="flex items-center gap-1 text-xs font-bold px-3 py-1"
                    style={{
                      background: "#A1E1B9",
                      border: "1.5px solid #242220",
                      borderRadius: "99px",
                      color: "#242220",
                    }}
                  >
                    <Award size={12} strokeWidth={2.5} /> Teljesített
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Progress bar */}
          <div className="mt-6">
            <div className="flex justify-between text-xs font-bold mb-2" style={{ color: "#242220" }}>
              <span>Haladás</span>
              <span>{completedIds.length}/{lessons.length} lecke teljesítve</span>
            </div>
            <div
              className="w-full h-4 relative overflow-hidden"
              style={{ background: "#fff", borderRadius: "99px", border: "1.5px solid #242220" }}
            >
              <div
                style={{
                  width: `${pct}%`,
                  background: "#A1E1B9",
                  height: "100%",
                  borderRadius: "99px",
                  transition: "width 0.6s ease",
                }}
              />
            </div>
          </div>
        </div>

        {/* Lessons list */}
        <h2 className="text-xl font-bold mb-4" style={{ color: "#242220" }}>
          Leckék
        </h2>

        <div className="space-y-3 mb-6">
          {lessons.map((lesson, idx) => {
            const done = completedIds.includes(lesson.id);
            const canAccess = idx === 0 || completedIds.includes(lessons[idx - 1]?.id);

            return (
              <div
                key={lesson.id}
                className="flex items-center gap-4 p-4 transition-all"
                style={{
                  background: "#fff",
                  borderRadius: "16px",
                  border: "1.5px solid #242220",
                  boxShadow: canAccess ? "4px 4px 0 #242220" : "2px 2px 0 #ccc",
                  opacity: canAccess ? 1 : 0.6,
                  cursor: canAccess ? "pointer" : "not-allowed",
                }}
                onClick={() =>
                  canAccess &&
                  navigate(`/courses/${courseId}/lessons/${lesson.id}`)
                }
                data-testid={`lesson-row-${idx}`}
              >
                <div
                  className="w-10 h-10 flex items-center justify-center flex-shrink-0"
                  style={{
                    borderRadius: "12px",
                    border: "1.5px solid #242220",
                    background: done ? "#A1E1B9" : canAccess ? "#FBD556" : "#e0e0e0",
                  }}
                >
                  {done ? (
                    <CheckCircle2 size={18} strokeWidth={2.5} color="#242220" />
                  ) : !canAccess ? (
                    <Lock size={16} strokeWidth={2.5} color="#999" />
                  ) : (
                    <span className="font-bold text-sm" style={{ color: "#242220" }}>
                      {lesson.order}
                    </span>
                  )}
                </div>

                <div className="flex-1">
                  <p className="font-bold text-sm" style={{ color: "#242220" }}>
                    {lesson.title}
                  </p>
                  <p className="text-xs" style={{ color: "#242220", opacity: 0.5 }}>
                    {lesson.quiz_questions?.length || 0} kérdéses kvíz
                    {done && " · Teljesítve"}
                  </p>
                </div>

                {canAccess && (
                  <ChevronRight size={18} strokeWidth={2.5} color="#242220" style={{ opacity: 0.4 }} />
                )}
              </div>
            );
          })}
        </div>

        {/* Final Exam CTA */}
        {lessons.length > 0 && (
          <div
            className="p-6"
            style={{
              background: examPassed ? "#A1E1B9" : allLessonsDone ? "#FF9456" : "#f5f5f5",
              borderRadius: "24px",
              border: "1.5px solid #242220",
              boxShadow: "6px 6px 0 #242220",
            }}
            data-testid="final-exam-section"
          >
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <h3 className="font-bold text-lg" style={{ color: "#242220" }}>
                  {examPassed ? "Záróvizsga Teljesítve!" : "Záróvizsga"}
                </h3>
                <p className="text-sm" style={{ color: "#242220", opacity: 0.7 }}>
                  {examPassed
                    ? `Eredmény: ${progress?.exam_score}% · Bizonyítvány elérhető`
                    : allLessonsDone
                    ? "Minden lecke teljesítve. Kezdd el a záróvizsgát!"
                    : "Teljesítsd az összes leckét a záróvizsga megkezdéséhez."}
                </p>
              </div>
              <button
                disabled={!allLessonsDone && !examPassed}
                onClick={() =>
                  examPassed
                    ? navigate(`/courses/${courseId}/certificate`)
                    : navigate(`/courses/${courseId}/exam`)
                }
                className="flex items-center gap-2 px-6 py-3 font-bold text-sm transition-all hover:translate-x-[1px] hover:translate-y-[1px] active:translate-x-[3px] active:translate-y-[3px]"
                style={{
                  background: allLessonsDone || examPassed ? "#FBD556" : "#e0e0e0",
                  borderRadius: "30px",
                  border: "1.5px solid #242220",
                  boxShadow: allLessonsDone || examPassed ? "3px 3px 0 #242220" : "none",
                  color: "#242220",
                  cursor: allLessonsDone || examPassed ? "pointer" : "not-allowed",
                }}
                data-testid="start-exam-btn"
              >
                {examPassed ? (
                  <><Award size={16} strokeWidth={2.5} /> Bizonyítvány letöltése</>
                ) : (
                  <>Záróvizsga <ChevronRight size={15} strokeWidth={2.5} /></>
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
